<template>
  <label
    class="cn-select"
    :class="{
      'cn-select--disabled': disabled,
    }"
  >
    <div class="cn-select__wrapper">
      <span v-if="label" class="cn-select__label">
        {{ label }}
      </span>
      <select
        id="test"
        v-model="valueLocal"
        name="test"
        class="cn-select__element"
        :disabled="disabled"
      >
        <option
          v-if="placeholder"
          value="null"
          disabled
          selected
          class="cn-select__placeholder"
        >
          {{ placeholder }}
        </option>
        <option v-for="option in options" :key="option.id" :value="option.id">
          {{ option.name }}
        </option>
      </select>
      <button
        v-if="clearable && valueLocal"
        class="cn-select__clear"
        @click="clear"
      >
        <UiIcon name="close" />
      </button>
    </div>
    <p v-if="error" class="cn-select__error">
      {{ error }}
    </p>
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  label?: string;
  placeholder?: string;
  options: {
    id: string;
    name: string;
  }[];
  modelValue?: string | number | null;
  clearable?: boolean;
  disabled?: boolean;
  error?: string;
}
const props = withDefaults(defineProps<Props>(), {
  clearable: false,
  disabled: false,
  error: '',
  placeholder: '',
  modelValue: null,
  label: '',
});
const emit = defineEmits<{
  (e: 'update:modelValue', data: string | undefined | null): void;
}>();
const valueLocal = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});

const clear = () => {
  valueLocal.value = null;
};
</script>
<style lang="scss">
.cn-select {
  --ab-canyon-select-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-select-text-color: var(--ab-canyon-color-dark);
  --ab-canyon-select-label-color-text-disabled: var(--ab-canyon-color-neutral);
  --ab-canyon-select-element-color-border: var(--ab-canyon-color-neutral);
  --ab-canyon-select-element-color-outline-focused: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-select-element-color-border-focused: var(
    --ab-canyon-color-neutral-dark
  );
  --ab-canyon-select-element-color-text-disabled: var(
    --ab-canyon-color-neutral
  );
  --ab-canyon-select-placeholder-color-text: var(
    --ab-canyon-color-neutral-dark
  );
  --ab-canyon-select-clear-color-text: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-select-error-color-text: var(--ab-canyon-color-accent);

  width: 100%;
  display: block;
  position: relative;
  &__wrapper {
    position: relative;
  }
  &__label {
    font-size: 0.75rem;
    line-height: 1rem;
    padding-left: calc(var(--size-base) / 4);
    padding-right: calc(var(--size-base) / 4);
    color: var(--ab-canyon-select-text-color);
    background-color: var(--ab-canyon-select-color-bg);
    white-space: nowrap;
    z-index: 10;
    left: 0.875rem;
    top: -0.375rem;
    position: absolute;
  }
  &__element {
    transition-property:
      color, background-color, border-color, text-decoration-color, fill,
      stroke, opacity, box-shadow, transform, filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    padding-top: var(--size-xs);
    padding-bottom: var(--size-xs);
    padding-left: var(--size-base);
    padding-right: var(--size-base);
    color: var(--ab-canyon-select-text-color);
    background-color: var(--ab-canyon-select-color-bg);
    border-color: var(--ab-canyon-select-element-color-border);
    cursor: pointer;
    width: 100%;
    display: block;
    &:focus {
      outline-color: var(--ab-canyon-select-element-color-outline-focused);
      outline-offset: 0px;
      border-color: var(--ab-canyon-select-element-color-border-focused);
    }
  }
  &__placeholder {
    color: var(--ab-canyon-select-placeholder-color-text);
  }
  &__clear {
    transform: translate(0, -50%);
    top: 50%;
    right: 2.25rem;
    position: absolute;
    color: var(--ab-canyon-select-clear-color-text);

    .cn-icon {
      width: var(--size-base);
      height: var(--size-base);
    }
  }
  &__error {
    color: var(--ab-canyon-select-error-color-text);
    padding-top: calc(var(--size-base) / 4);
    padding-bottom: calc(var(--size-base) / 4);
  }
  &--disabled {
    .cn-select {
      &__label {
        color: var(--ab-canyon-select-label-color-text-disabled);
      }
      &__element {
        color: var(--ab-canyon-select-element-color-text-disabled);
        cursor: not-allowed;
      }
    }
  }
}
</style>
